import React, { useEffect, useMemo, useState } from "react";
import { Button, DatePicker, Input, Select, Spin, message, Table } from "antd";
import { ExportOutlined, PlusOutlined } from "@ant-design/icons";
import "./index.css";
import AppModal from "../../modals/AppModal";
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAppDispatch, useAppSelector } from "../../../store/hooks";
import { useExporter } from "../../../hooks/useExporter";
import MemoCard, { MemoCardModel } from "./MemoCard";
import type { ColumnsType } from "antd/es/table";

// TODO: implement memo.slice similar to media.slice
import {
  fetchMemos,
  setMemoProjectId,
  setMemoSearch,
  setMemoCreatedDate,
  setMemoGroupId,
  setMemoPage,
  setMemoPageSize,
  setDeletingMemoId,
  deleteMemo,
  fetchMemoGroups,
  createMemo,
  createMemoGroups,
  fetchMemoPreview,
  addMemoToGroups,
  updateMemo,
  fetchMemoDetails,
  linkMemoEntity,
  unlinkMemoEntity,
} from "./memo.slice";
import LinkedMediaModalForMemo from "./LinkedMediaModalForMemo";

const { Search } = Input;

const Memo: React.FC = () => {
  const { t } = useTranslation("docProcessing");
  const { id: projectId } = useParams();
  const dispatch = useAppDispatch();

  const {
    items,
    total,
    page,
    pageSize,
    loading,
    search,
    createdDate,
    groupId,
    groups,
    deletingId,
    deletingLoading,
    creating,
    previewById,
    previewLoadingById,
    previewErrorById,
    creatingGroup,
    creatingMemo,
    addingToGroup,
    updatingMemo,
    detailsById,
    detailsLoadingById,
    linkableItems,
  } = useAppSelector((s: any) => s.memo);

  const memoProjectId = useAppSelector((s: any) => s.memo.projectId); // e marr nga store
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [editTitle, setEditTitle] = useState("");
  const [isAddGroupOpen, setIsAddGroupOpen] = useState(false);
  const [newGroupNames, setNewGroupNames] = useState<string[]>([""]);
  const [linkedOpen, setLinkedOpen] = useState(false);
  const [linkedMemo, setLinkedMemo] = useState<MemoCardModel | null>(null);
  const { exporting, exportNow } = useExporter({
    endpoint: "/api/memos/export/excel",
    fallbackPrefix: "memos",
    ext: "xlsx",
  });

  const [isAddMemoOpen, setIsAddMemoOpen] = useState(false);
  const [newMemoTitle, setNewMemoTitle] = useState("");
  const [newMemoDesc, setNewMemoDesc] = useState("");
  const [newMemoGroupIds, setNewMemoGroupIds] = useState<string[]>([]);

  const [isAddToGroupOpen, setIsAddToGroupOpen] = useState(false);
  const [groupSearch, setGroupSearch] = useState("");
  const [selectedGroupIds, setSelectedGroupIds] = useState<string[]>([]);
  const [pendingMemoIds, setPendingMemoIds] = useState<string[]>([]);

  const [editId, setEditId] = useState<string | null>(null);
  const [editDesc, setEditDesc] = useState("");
  const [editGroupIds, setEditGroupIds] = useState<string[]>([]);
  const detailsLoading = editId ? !!detailsLoadingById?.[editId] : false;

  useEffect(() => {
    dispatch(setMemoProjectId(projectId ?? null));
  }, [dispatch, projectId]);

  useEffect(() => {
    if (!memoProjectId) return;
    dispatch(fetchMemoGroups());
  }, [dispatch, memoProjectId]);

  useEffect(() => {
    if (!projectId) return;
    dispatch(fetchMemos());
  }, [dispatch, projectId, page, pageSize, search, createdDate, groupId]);

  const groupOptions = useMemo(
    () => (groups || []).map((g: any) => ({ label: g.name, value: g.id })),
    [groups]
  );

  const filteredGroups = useMemo(() => {
    const q = groupSearch.trim().toLowerCase();
    if (!q) return groups || [];
    return (groups || []).filter((g: any) =>
      String(g?.name || "")
        .toLowerCase()
        .includes(q)
    );
  }, [groups, groupSearch]);

  const formatMemoDate = (iso?: string) => {
    if (!iso) return "—";
    const d = new Date(iso);
    return d.toLocaleString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const columns: ColumnsType<any> = [
    {
      title: t("memo.table.memoGroup"), // ✅ (optional) label according to Figma
      dataIndex: "name",
      key: "name",
    },
    {
      title: t("memo.table.numberOfMemos"),
      dataIndex: "memo_count",
      key: "memo_count",
      render: (v) => v ?? 0,
    },
  ];

  const openLinkedMedia = (memo: MemoCardModel) => {
    setLinkedMemo(memo);
    setLinkedOpen(true);
  };

  const closeLinkedMedia = () => {
    setLinkedOpen(false);
    setLinkedMemo(null);
  };

  const onCheck = (id: string, checked: boolean) => {
    setSelectedIds((prev) => {
      if (checked) return prev.includes(id) ? prev : [...prev, id];
      return prev.filter((x) => x !== id);
    });
  };

  const handleExport = async () => {
    if (!projectId) return;

    const params: Record<string, any> = {
      project_id: projectId,
      search: search || undefined,
      group_id: groupId || undefined,
    };

    if (createdDate) {
      params.date_from = createdDate;
      params.date_to = createdDate;
    }

    await exportNow({
      params,
      accept:
        "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });
  };

  const handleDeleteConfirm = async () => {
    if (!deletingId) return;
    try {
      await dispatch(deleteMemo(deletingId)).unwrap();
      message.success(t("memo.deleteSuccess"));
      dispatch(setDeletingMemoId(null));
      dispatch(fetchMemos());
      setSelectedIds((prev) => prev.filter((x) => x !== deletingId));
    } catch (e: any) {
      message.error(e?.message || t("memo.deleteError"));
    }
  };

  const openMemo = (memo: MemoCardModel) => {
    message.info(t("memo.openMemo", { value: memo.title || memo.id }));
  };

  const editMemo = async (memo: MemoCardModel) => {
    setEditId(memo.id);
    setIsEditOpen(true); // hap modalin, mundesh edhe pas fetch-it
    setEditTitle("");
    setEditDesc("");
    setEditGroupIds([]);
    try {
      const { details } = await dispatch(
        fetchMemoDetails({ memoId: memo.id })
      ).unwrap();

      setEditTitle(details.title || "");
      setEditDesc(details.description || "");

      // ✅ group ids prej backend-it
      const groupIds = (details.groups || [])
        .map((g) => g?.id)
        .filter(Boolean) as string[];

      setEditGroupIds(groupIds);
    } catch (e: any) {
      message.error(e?.message || t("memo.loadDetailsError"));
      // optional: mbylle modalin nese s’ka data
      // setIsEditOpen(false);
    }
  };

  const addToGroup = (memo: MemoCardModel) => {
    const memoIdsToAdd = selectedIds.length ? selectedIds : [memo.id];

    setPendingMemoIds(memoIdsToAdd);
    setSelectedGroupIds([]);
    setGroupSearch("");
    setIsAddToGroupOpen(true);
  };

  const askDelete = (memo: MemoCardModel) => {
    dispatch(setDeletingMemoId(memo.id));
  };

  const closeAddGroupModal = () => {
    setIsAddGroupOpen(false);
    setNewGroupNames([""]);
  };

  const handleCreateGroups = async () => {
    if (!projectId) return;

    const names = newGroupNames.map((n) => n.trim()).filter(Boolean);
    if (names.length === 0) {
      message.error(t("memo.groupNameRequired"));
      return;
    }

    try {
      await dispatch(createMemoGroups({ projectId, names })).unwrap();
      message.success(t("memo.groupsCreated"));
      closeAddGroupModal();
      dispatch(fetchMemoGroups());
    } catch (e: any) {
      message.error(e?.message || t("memo.createGroupsError"));
    }
  };

  const closeAddMemoModal = () => {
    setIsAddMemoOpen(false);
    setNewMemoTitle("");
    setNewMemoDesc("");
    setNewMemoGroupIds([]);
  };

  const handleCreateMemo = async () => {
    if (!projectId) return;

    if (!newMemoTitle.trim()) {
      message.error(t("memo.memoTitleRequired"));
      return;
    }

    try {
      await dispatch(
        createMemo({
          projectId,
          title: newMemoTitle.trim(),
          description: newMemoDesc?.trim() || "",
          groupIds: newMemoGroupIds || [],
        })
      ).unwrap();

      message.success(t("memo.memoCreated"));
      closeAddMemoModal();
      dispatch(fetchMemos());
    } catch (e: any) {
      message.error(e?.message || t("memo.createMemoError"));
    }
  };

  const handleUpdateMemo = async () => {
    if (!editId) return;

    if (!editTitle.trim()) {
      message.error(t("memo.memoTitleRequired"));
      return;
    }

    try {
      await dispatch(
        updateMemo({
          memoId: editId,
          title: editTitle.trim(),
          description: editDesc?.trim() || "",
          groupIds: editGroupIds || [],
        })
      ).unwrap();

      message.success(t("memo.memoUpdated"));
      setIsEditOpen(false);

      dispatch(fetchMemos());
      dispatch(fetchMemoGroups());
    } catch (e: any) {
      message.error(e?.message || t("memo.updateMemoError"));
    }
  };

  const handlePreviewOpen = (memoId: string) => {
    const alreadyHave = previewById?.[memoId];
    const isLoading = previewLoadingById?.[memoId];
    if (alreadyHave || isLoading) return;
    dispatch(fetchMemoPreview({ memoId }));
  };

  const closeAddToGroupModal = () => {
    if (addingToGroup) return;
    setIsAddToGroupOpen(false);
    setGroupSearch("");
    setSelectedGroupIds([]);
    setPendingMemoIds([]);
  };

  // ✅ UPDATED to match endpoint: POST /api/memos/{memo_id}/groups with { group_ids: [] }
  const handleAddMemosToGroups = async () => {
    if (!pendingMemoIds.length) {
      message.error(t("memo.noMemosSelected"));
      return;
    }

    if (!selectedGroupIds.length) {
      message.error(t("memo.selectAtLeastOneGroup"));
      return;
    }

    try {
      // for each memo -> attach selected group ids
      await Promise.all(
        pendingMemoIds.map((memoId) =>
          dispatch(
            addMemoToGroups({ memoId, groupIds: selectedGroupIds })
          ).unwrap()
        )
      );

      message.success(t("memo.memosAddedToGroups"));
      closeAddToGroupModal();
      dispatch(fetchMemos());
      dispatch(fetchMemoGroups());
    } catch (e: any) {
      message.error(e?.message || t("memo.addToGroupsError"));
    }
  };

  return (
    <div className="memo-page">
      <div className="memo-card">
        {/* Header */}
        <div className="memo-header">
          <div>
            <h2 className="memo-title">{t("memo.title")}</h2>
          </div>

          <div className="memo-header-actions">
            <Button
              className="memo-btn memo-btn-outline"
              onClick={handleExport}
              icon={<ExportOutlined />}
            >
              {exporting ? t("memo.exporting") : t("memo.export")}
            </Button>

            <Button
              className="memo-btn memo-btn-light"
              icon={<PlusOutlined />}
              onClick={() => setIsAddGroupOpen(true)}
            >
              {t("memo.addGroup")}
            </Button>

            <Button
              className="memo-btn memo-btn-primary"
              icon={<PlusOutlined />}
              onClick={() => setIsAddMemoOpen(true)}
            >
              {t("memo.addNewMemo")}
            </Button>
          </div>
        </div>

        <p className="memo-subtitle">{t("memo.subtitle")}</p>

        {/* Filters */}
        <div className="memo-filters">
          <div className="memo-filter">
            <label className="memo-filter-label">{t("memo.filters.search")}</label>
            <Search
              placeholder={t("memo.filters.searchPlaceholder")}
              allowClear
              value={search || ""}
              onChange={(e) => dispatch(setMemoSearch(e.target.value))}
              onSearch={(val) => dispatch(setMemoSearch(val))}
            />
          </div>

          <div className="memo-filter">
            <label className="memo-filter-label">{t("memo.filters.dateCreated")}</label>
            <DatePicker
              className="memo-date"
              allowClear
              placeholder={t("memo.filters.datePlaceholder")}
              onChange={(d: any) =>
                dispatch(setMemoCreatedDate(d ? d.format("YYYY-MM-DD") : null))
              }
            />
          </div>

          <div className="memo-filter">
            <label className="memo-filter-label">{t("memo.filters.memoGroup")}</label>
            <Select
              allowClear
              placeholder={t("memo.filters.selectOption")}
              value={groupId}
              options={groupOptions}
              onChange={(val) => dispatch(setMemoGroupId(val ?? null))}
            />
          </div>
        </div>

        {/* Grid */}
        {loading ? (
          <div className="memo-loading">
            <Spin size="large" />
          </div>
        ) : (
          <div className="memo-grid">
            {/* {console.log("items", items)}
            {items?.map((m: any) => (
              <MemoCard
                key={m.id}
                memo={m}
                checked={selectedIds.includes(m.id)}
                onCheck={onCheck}
                onOpen={openMemo}
                onEdit={editMemo}
                onAddToGroup={addToGroup}
                onDelete={askDelete}
                onLink={openLinkedMedia}
                onPreviewOpen={handlePreviewOpen}
                preview={previewById?.[m.id] ?? null}
                previewLoading={!!previewLoadingById?.[m.id]}
                previewError={previewErrorById?.[m.id] ?? null}
              />
            ))} */}
            {items?.map((m: any) => {
              const memoModel: MemoCardModel = {
                id: m.id,
                title: m.title,
                preview: m.preview,
                content: m.content,
                createdAt: formatMemoDate(m.created_at || m.createdAt),
                createdBy: String(m.created_by ?? m.createdBy ?? "—"),
              };

              return (
                <MemoCard
                  key={m.id}
                  memo={memoModel}
                  checked={selectedIds.includes(m.id)}
                  onCheck={onCheck}
                  onOpen={openMemo}
                  onEdit={editMemo}
                  onAddToGroup={addToGroup}
                  onDelete={askDelete}
                  onLink={openLinkedMedia}
                  onPreviewOpen={handlePreviewOpen}
                  preview={previewById?.[m.id] ?? null}
                  previewLoading={!!previewLoadingById?.[m.id]}
                  previewError={previewErrorById?.[m.id] ?? null}
                />
              );
            })}
          </div>
        )}
      </div>

      {/* Add group modal */}
      <AppModal
        open={isAddGroupOpen}
        loading={creatingGroup}
        className="add-groupmemo-modal-style"
        onCancel={() => {
          if (creatingGroup) return;
          closeAddGroupModal();
        }}
        onConfirm={async () => {
          if (!projectId) return;

          const names = newGroupNames.map((n) => n.trim()).filter(Boolean);
          if (names.length === 0) {
            message.error(t("memo.groupNameRequired"));
            return;
          }

          try {
            await dispatch(createMemoGroups({ projectId, names })).unwrap();
            message.success(t("memo.groupsCreated"));
            closeAddGroupModal();
            dispatch(fetchMemoGroups());
          } catch (e: any) {
            message.error(e?.message || t("memo.createGroupsError"));
          }
        }}
        title={t("memo.addGroupModal.title")}
        description={
          <div style={{ marginTop: 8 }}>
            <div
              style={{
                fontWeight: 500,
                marginBottom: 10,
                marginTop: "1rem",
                textAlign: "left",
              }}
            >
              {t("memo.addGroupModal.addNewGroup")}
            </div>

            <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
              {newGroupNames.map((val, idx) => (
                <div
                  key={idx}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 10,
                    width: "100%",
                  }}
                >
                  <Input
                    placeholder={t("memo.addGroupModal.groupPlaceholder", {
                      index: idx + 1,
                    })}
                    value={val}
                    disabled={creating}
                    onChange={(e) => {
                      const next = [...newGroupNames];
                      next[idx] = e.target.value;
                      setNewGroupNames(next);
                    }}
                  />

                  <Button
                    disabled={creating || newGroupNames.length === 1}
                    onClick={() => {
                      if (newGroupNames.length === 1) return;
                      setNewGroupNames((prev) =>
                        prev.filter((_, i) => i !== idx)
                      );
                    }}
                  >
                    -
                  </Button>

                  <Button
                    disabled={creating}
                    onClick={() => setNewGroupNames((prev) => [...prev, ""])}
                  >
                    +
                  </Button>
                </div>
              ))}
            </div>
          </div>
        }
        footer={
          <div className="add-memogroup-footer">
            <Button
              className="cancel-button"
              onClick={closeAddGroupModal}
              disabled={creatingGroup}
            >
              {t("memo.addGroupModal.cancel")}
            </Button>

            <Button
              className="add-button"
              type="primary"
              onClick={handleCreateGroups}
              loading={creatingGroup}
            >
              {t("memo.addGroupModal.add")}
            </Button>
          </div>
        }
        confirmType="primary"
      />

      {/* Add memo modal */}
      <AppModal
        open={isAddMemoOpen}
        loading={false}
        className="add-memo-modal-style"
        onCancel={() => setIsAddMemoOpen(false)}
        onConfirm={async () => {
          if (!projectId) return;

          if (!newMemoTitle.trim()) {
            message.error(t("memo.memoTitleRequired"));
            return;
          }

          try {
            await dispatch(
              createMemo({
                projectId,
                title: newMemoTitle.trim(),
                description: newMemoDesc || "",
                groupIds: newMemoGroupIds || [],
              })
            ).unwrap();

            message.success(t("memo.memoCreated"));
            setIsAddMemoOpen(false);
            setNewMemoTitle("");
            setNewMemoDesc("");
            setNewMemoGroupIds([]);

            dispatch(fetchMemos());
          } catch (e: any) {
            message.error(e?.message || t("memo.createMemoError"));
          }
        }}
        title={t("memo.addMemoModal.title")}
        description={
          <div
            style={{
              marginTop: 8,
              display: "flex",
              flexDirection: "column",
              gap: "1.5rem",
            }}
          >
            <div>
              <div
                style={{
                  fontWeight: 500,
                  marginBottom: 6,
                  marginTop: "1rem",
                  textAlign: "left",
                }}
              >
                <span style={{ color: "red" }}>*</span>{" "}
                {t("memo.addMemoModal.memoGroupLabel")}
              </div>
              <Select
                mode="multiple"
                allowClear
                placeholder={t("memo.addMemoModal.selectGroups")}
                options={groupOptions}
                value={newMemoGroupIds}
                onChange={(vals) => setNewMemoGroupIds(vals)}
                style={{ width: "100%" }}
              />
            </div>

            <div>
              <div
                style={{ fontWeight: 500, marginBottom: 6, textAlign: "left" }}
              >
                <span style={{ color: "red" }}>*</span>{" "}
                {t("memo.addMemoModal.memoTitleLabel")}
              </div>
              <Input
                placeholder={t("memo.addMemoModal.titlePlaceholder")}
                value={newMemoTitle}
                onChange={(e) => setNewMemoTitle(e.target.value)}
              />
            </div>

            <div>
              <div
                style={{
                  fontWeight: 500,
                  marginBottom: 6,
                  textAlign: "left",
                  fontFamily: "Poppins",
                }}
              >
                {t("memo.addMemoModal.descriptionLabel")}
              </div>
              <Input.TextArea
                placeholder={t("memo.addMemoModal.descriptionPlaceholder")}
                value={newMemoDesc}
                onChange={(e) => setNewMemoDesc(e.target.value)}
                maxLength={100}
                showCount
                rows={4}
              />
            </div>
          </div>
        }
        footer={
          <div className="add-memogroup-footer">
            <Button
              className="cancel-button"
              onClick={closeAddMemoModal}
              disabled={creatingMemo}
            >
              {t("memo.addMemoModal.cancel")}
            </Button>

            <Button
              className="add-button"
              type="primary"
              onClick={handleCreateMemo}
              loading={creatingMemo}
              // loading={creating}
            >
              {t("memo.addMemoModal.add")}
            </Button>
          </div>
        }
        confirmType="primary"
      />

      {/* Edit memo modal */}
      <AppModal
        open={isEditOpen}
        loading={updatingMemo || detailsLoading}
        onCancel={() => {
          if (updatingMemo || detailsLoading) return;
          setIsEditOpen(false);
        }}
        className="edit-memo-modal-style"
        onConfirm={async () => {
          if (!editId) return;

          if (!editTitle.trim()) {
            message.error(t("memo.memoTitleRequired"));
            return;
          }

          try {
            await dispatch(
              updateMemo({
                memoId: editId,
                title: editTitle.trim(),
                description: editDesc?.trim() || "",
                groupIds: editGroupIds || [],
              })
            ).unwrap();

            message.success(t("memo.memoUpdated"));
            setIsEditOpen(false);

            dispatch(fetchMemos());
            dispatch(fetchMemoGroups());
          } catch (e: any) {
            message.error(e?.message || t("memo.updateMemoError"));
          }
        }}
        title={t("memo.editMemoModal.title")}
        description={
          <div
            style={{
              marginTop: 8,
              display: "flex",
              flexDirection: "column",
              gap: 14,
            }}
          >
            <div>
              <div style={{ fontWeight: 500, marginBottom: 6 }}>
                <span style={{ color: "red" }}>*</span>{" "}
                {t("memo.editMemoModal.memoGroupLabel")}
              </div>
              <Select
                mode="multiple"
                allowClear
                placeholder={t("memo.editMemoModal.selectGroups")}
                options={groupOptions}
                value={editGroupIds}
                onChange={(vals) => setEditGroupIds(vals)}
                style={{ width: "100%" }}
                disabled={updatingMemo || detailsLoading}
              />
            </div>

            <div>
              <div style={{ fontWeight: 500, marginBottom: 6 }}>
                <span style={{ color: "red" }}>*</span>{" "}
                {t("memo.editMemoModal.memoTitleLabel")}
              </div>
              <Input
                value={editTitle}
                onChange={(e) => setEditTitle(e.target.value)}
                disabled={updatingMemo || detailsLoading}
              />
            </div>

            <div>
              <div style={{ fontWeight: 500, marginBottom: 6 }}>
                {t("memo.editMemoModal.descriptionLabel")}
              </div>
              <Input.TextArea
                value={editDesc}
                onChange={(e) => setEditDesc(e.target.value)}
                rows={4}
                disabled={updatingMemo || detailsLoading}
              />
            </div>
          </div>
        }
        confirmText={t("memo.editMemoModal.saveChanges")}
        // cancelText="Cancel"
        // confirmType="primary"
        footer={
          <div className="add-memogroup-footer">
            <Button
              className="cancel-button"
              onClick={() => setIsEditOpen(false)}
              disabled={updatingMemo || detailsLoading}
            >
              {t("memo.editMemoModal.cancel")}
            </Button>

            <Button
              className="add-button"
              type="primary"
              onClick={handleUpdateMemo}
              loading={updatingMemo || detailsLoading}
            >
              {t("memo.editMemoModal.saveChanges")}
            </Button>
          </div>
        }
      />

      {/* Delete modal */}
      <AppModal
        open={!!deletingId}
        loading={deletingLoading}
        onCancel={() => dispatch(setDeletingMemoId(null))}
        onConfirm={handleDeleteConfirm}
        title={t("memo.deleteModal.title")}
        description={t("memo.deleteModal.description")}
        subDescription={t("memo.deleteModal.subDescription")}
        confirmText={t("memo.deleteModal.confirm")}
        cancelText={t("memo.deleteModal.cancel")}
        confirmType="primary"
        danger
      />

      {/**Add memo to group */}
      <AppModal
        open={isAddToGroupOpen}
        loading={addingToGroup}
        title={t("memo.addToGroupModal.title")}
        className="add-memo-to-group-modal-style"
        onCancel={closeAddToGroupModal}
        onConfirm={handleAddMemosToGroups}
        description={
          <div style={{ marginTop: 8 }}>
            <div style={{ marginBottom: 12, opacity: 0.8 }}>
              {t("memo.addToGroupModal.description")}
            </div>

            <div style={{ marginBottom: 12 }}>
              <div
                style={{
                  fontWeight: 500,
                  marginBottom: 6,
                  textAlign: "left",
                  fontFamily: "Poppins",
                }}
              >
                {t("memo.addToGroupModal.searchGroupsLabel")}
              </div>
              <Input
                placeholder={t("memo.addToGroupModal.searchPlaceholder")}
                value={groupSearch}
                onChange={(e) => setGroupSearch(e.target.value)}
                disabled={addingToGroup}
              />
            </div>

            <Table
              rowKey="id"
              columns={columns}
              dataSource={filteredGroups}
              pagination={false}
              size="small"
              rowSelection={{
                selectedRowKeys: selectedGroupIds,
                onChange: (keys) => setSelectedGroupIds(keys as string[]),
                getCheckboxProps: () => ({ disabled: addingToGroup }),
              }}
            />
          </div>
        }
        footer={
          <div className="add-memogroup-footer">
            <Button
              className="cancel-button"
              onClick={closeAddToGroupModal}
              disabled={addingToGroup}
            >
              {t("memo.addToGroupModal.cancel")}
            </Button>

            <Button
              className="add-button"
              type="primary"
              onClick={handleAddMemosToGroups}
              loading={addingToGroup}
            >
              {t("memo.addToGroupModal.add")}
            </Button>
          </div>
        }
      />

      {/* <LinkedMediaModalForMemo
        open={linkedOpen}
        memo={linkedMemo}
        groupOptions={groupOptions}
        onClose={closeLinkedMedia}
        onSubmit={() => {
          message.success("Linked media saved (static)");
          closeLinkedMedia();
        }}
      /> */}
      <LinkedMediaModalForMemo
        open={linkedOpen}
        memo={linkedMemo}
        groupOptions={groupOptions}
        onClose={closeLinkedMedia}
        onToggleLink={async (entityId, nextLinked) => {
          if (!linkedMemo?.id) return;

          const row = (linkableItems || []).find((x: any) => x.id === entityId);
          if (!row) throw new Error("Entity not found in list");

          if (nextLinked) {
            await dispatch(
              linkMemoEntity({
                memoId: linkedMemo.id,
                linked_type: row.type, // ✅ "document|highlight|code|descriptor"
                linked_entity_id: row.id, // ✅ entityId
              })
            ).unwrap();
          } else {
            if (!row.link_id)
              throw new Error(
                "Missing link_id (API must return it for linked rows)"
              );
            await dispatch(
              unlinkMemoEntity({
                memoId: linkedMemo.id,
                linkId: row.link_id,
              })
            ).unwrap();
          }
        }}
      />
    </div>
  );
};

export default Memo;
